import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils"

/**
 * Кнопки по макетам: высота 40px, радиус 12px.
 * - default (= primary) — жёлтая заливка accent + текст on-accent; главное действие экрана/диалога;
 * - secondary / outline — surface + рамка border («Отмена», «Экспорт CSV»);
 * - ghost — без рамки, подсветка muted при наведении;
 * - destructive — сплошная neg (подтверждение удаления/блокировки);
 * - destructiveOutline — рамка и текст neg («Заблокировать…», открывает подтверждение);
 * - inverse — тёмная заливка inverse;
 * - link — текст accent-text («Все платежи →»).
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold transition-[background-color,border-color,color,box-shadow,filter] outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-accent text-on-accent font-bold hover:brightness-95 active:brightness-90",
        primary: "bg-accent text-on-accent font-bold hover:brightness-95 active:brightness-90",
        secondary: "border border-border bg-surface text-text hover:bg-surface-alt",
        outline: "border border-border bg-surface text-text hover:bg-surface-alt",
        ghost: "text-text hover:bg-muted",
        destructive: "bg-neg text-on-neg font-bold hover:brightness-95 active:brightness-90",
        destructiveOutline: "border border-neg bg-transparent text-neg hover:bg-neg-soft",
        inverse: "bg-inverse text-on-inverse font-bold hover:opacity-90",
        link: "h-auto! px-0! text-accent-text underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4",
        sm: "h-8 rounded-[10px] px-3 text-[13px]",
        lg: "h-[46px] px-5 text-[15px]",
        icon: "size-10",
        "icon-sm": "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props} />
    );
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
